import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import './AdminModern.css';

const AdminSettings = () => {
  const [settings, setSettings] = useState({
    system: {
      maintenance_mode: false,
      allow_anonymous_chat: true,
      max_message_length: 2000,
      session_timeout: 30,
      auto_save_interval: 5,
    },
    ai: {
      model_temperature: 0.7,
      max_tokens: 1000,
      response_timeout: 30,
      enable_streaming: true,
      language_detection: true,
    },
    security: {
      rate_limiting: true,
      max_requests_per_minute: 60,
      enable_content_filter: true,
      log_level: 'INFO',
      session_security: 'high',
    },
    notifications: {
      email_notifications: true,
      admin_alerts: true,
      error_reporting: true,
      performance_alerts: false,
    }
  });

  const [activeSection, setActiveSection] = useState('system');
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const updateSetting = (section, key, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value
      }
    }));
    setIsDirty(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      setIsDirty(false);
      // Show success notification
      console.log('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      system: {
        maintenance_mode: false,
        allow_anonymous_chat: true,
        max_message_length: 2000,
        session_timeout: 30,
        auto_save_interval: 5,
      },
      ai: {
        model_temperature: 0.7,
        max_tokens: 1000,
        response_timeout: 30,
        enable_streaming: true,
        language_detection: true,
      },
      security: {
        rate_limiting: true,
        max_requests_per_minute: 60,
        enable_content_filter: true,
        log_level: 'INFO',
        session_security: 'high',
      },
      notifications: {
        email_notifications: true,
        admin_alerts: true,
        error_reporting: true,
        performance_alerts: false,
      }
    });
    setIsDirty(true);
  };

  const settingSections = [
    { id: 'system', label: 'System', icon: 'settings' },
    { id: 'ai', label: 'AI Configuration', icon: 'brain' },
    { id: 'security', label: 'Security', icon: 'shield' },
    { id: 'notifications', label: 'Notifications', icon: 'bell' },
  ];

  const getIcon = (iconName) => {
    const icons = {
      settings: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3"/>
          <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24"/>
        </svg>
      ),
      brain: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"/>
          <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"/>
        </svg>
      ),
      shield: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
      ),
      bell: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21c-.13.14-.27.26-.41.36a2 2 0 0 1-2.64 0c-.14-.1-.28-.22-.41-.36"/>
        </svg>
      )
    };
    return icons[iconName] || icons.settings;
  };

  return (
    <motion.div 
      className="settings-container"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Settings Header */}
      <div className="settings-header">
        <div className="header-content">
          <div className="header-info">
            <h1>System Configuration</h1>
            <p>Manage system settings, AI configuration, and security options</p>
          </div>
          <div className="header-actions">
            {isDirty && (
              <motion.div 
                className="unsaved-indicator"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <span className="indicator-dot"></span>
                Unsaved changes
              </motion.div>
            )}
            <button 
              onClick={resetToDefaults}
              className="reset-btn"
              disabled={saving}
            >
              Reset to Defaults
            </button>
            <motion.button
              onClick={saveSettings}
              disabled={!isDirty || saving}
              className={`save-btn ${isDirty ? 'active' : ''}`}
              whileHover={isDirty ? { scale: 1.05 } : {}}
              whileTap={isDirty ? { scale: 0.95 } : {}}
            >
              {saving ? (
                <>
                  <div className="saving-spinner"></div>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </motion.button>
          </div>
        </div>
      </div>

      <div className="settings-layout">
        {/* Settings Navigation */}
        <div className="settings-nav">
          {settingSections.map((section, index) => (
            <motion.button
              key={section.id}
              className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => setActiveSection(section.id)}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ x: 4 }}
            >
              <div className="nav-icon">
                {getIcon(section.icon)}
              </div>
              <span className="nav-label">{section.label}</span>
            </motion.button>
          ))}
        </div>

        {/* Settings Content */}
        <motion.div 
          className="settings-content"
          key={activeSection}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          {activeSection === 'system' && (
            <div className="settings-section">
              <h2>System Settings</h2>
              <div className="settings-grid">
                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Maintenance Mode</h3>
                    <p>Temporarily disable the system for maintenance</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.system.maintenance_mode}
                      onChange={(e) => updateSetting('system', 'maintenance_mode', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Anonymous Chat</h3>
                    <p>Allow users to chat without signing in</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.system.allow_anonymous_chat}
                      onChange={(e) => updateSetting('system', 'allow_anonymous_chat', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Max Message Length</h3>
                    <p>Maximum characters allowed per message</p>
                  </div>
                  <div className="input-group">
                    <input
                      type="number"
                      value={settings.system.max_message_length}
                      onChange={(e) => updateSetting('system', 'max_message_length', parseInt(e.target.value))}
                      min="100"
                      max="10000"
                    />
                    <span className="input-suffix">characters</span>
                  </div>
                </div>

                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Session Timeout</h3>
                    <p>Minutes of inactivity before auto-logout</p>
                  </div>
                  <div className="input-group">
                    <input
                      type="number"
                      value={settings.system.session_timeout}
                      onChange={(e) => updateSetting('system', 'session_timeout', parseInt(e.target.value))}
                      min="5"
                      max="120"
                    />
                    <span className="input-suffix">minutes</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'ai' && (
            <div className="settings-section">
              <h2>AI Configuration</h2>
              <div className="settings-grid">
                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Model Temperature</h3>
                    <p>Controls randomness in AI responses (0.0 - 1.0)</p>
                  </div>
                  <div className="slider-group">
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={settings.ai.model_temperature}
                      onChange={(e) => updateSetting('ai', 'model_temperature', parseFloat(e.target.value))}
                      className="range-slider"
                    />
                    <span className="range-value">{settings.ai.model_temperature}</span>
                  </div>
                </div>

                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Max Tokens</h3>
                    <p>Maximum tokens in AI responses</p>
                  </div>
                  <div className="input-group">
                    <input
                      type="number"
                      value={settings.ai.max_tokens}
                      onChange={(e) => updateSetting('ai', 'max_tokens', parseInt(e.target.value))}
                      min="100"
                      max="4000"
                    />
                    <span className="input-suffix">tokens</span>
                  </div>
                </div>

                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Response Timeout</h3>
                    <p>Maximum time to wait for AI response</p>
                  </div>
                  <div className="input-group">
                    <input
                      type="number"
                      value={settings.ai.response_timeout}
                      onChange={(e) => updateSetting('ai', 'response_timeout', parseInt(e.target.value))}
                      min="10"
                      max="120"
                    />
                    <span className="input-suffix">seconds</span>
                  </div>
                </div>

                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Enable Streaming</h3>
                    <p>Stream responses for better user experience</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.ai.enable_streaming}
                      onChange={(e) => updateSetting('ai', 'enable_streaming', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'security' && (
            <div className="settings-section">
              <h2>Security Settings</h2>
              <div className="settings-grid">
                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Rate Limiting</h3>
                    <p>Protect against abuse with request limiting</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.security.rate_limiting}
                      onChange={(e) => updateSetting('security', 'rate_limiting', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Max Requests per Minute</h3>
                    <p>Maximum requests allowed per user per minute</p>
                  </div>
                  <div className="input-group">
                    <input
                      type="number"
                      value={settings.security.max_requests_per_minute}
                      onChange={(e) => updateSetting('security', 'max_requests_per_minute', parseInt(e.target.value))}
                      min="10"
                      max="300"
                      disabled={!settings.security.rate_limiting}
                    />
                    <span className="input-suffix">requests</span>
                  </div>
                </div>

                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Content Filter</h3>
                    <p>Filter inappropriate content automatically</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.security.enable_content_filter}
                      onChange={(e) => updateSetting('security', 'enable_content_filter', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Log Level</h3>
                    <p>System logging verbosity level</p>
                  </div>
                  <select 
                    value={settings.security.log_level}
                    onChange={(e) => updateSetting('security', 'log_level', e.target.value)}
                    className="select-input"
                  >
                    <option value="DEBUG">Debug</option>
                    <option value="INFO">Info</option>
                    <option value="WARNING">Warning</option>
                    <option value="ERROR">Error</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'notifications' && (
            <div className="settings-section">
              <h2>Notification Settings</h2>
              <div className="settings-grid">
                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Email Notifications</h3>
                    <p>Send notifications via email</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.notifications.email_notifications}
                      onChange={(e) => updateSetting('notifications', 'email_notifications', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Admin Alerts</h3>
                    <p>Receive alerts for critical system events</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.notifications.admin_alerts}
                      onChange={(e) => updateSetting('notifications', 'admin_alerts', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Error Reporting</h3>
                    <p>Automatically report system errors</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.notifications.error_reporting}
                      onChange={(e) => updateSetting('notifications', 'error_reporting', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-card">
                  <div className="setting-header">
                    <h3>Performance Alerts</h3>
                    <p>Get notified about system performance issues</p>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={settings.notifications.performance_alerts}
                      onChange={(e) => updateSetting('notifications', 'performance_alerts', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AdminSettings;